import React, { useEffect, useState } from "react";
import { MapPin, Phone, Mail, Clock, Facebook, Instagram } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { GhlEmbedPlaceholder } from "@/components/GhlEmbedPlaceholder";
import { SITE } from "@/constants/testIds";

export default function ContactPage() {
    const [info, setInfo] = useState(null);
    useEffect(() => {
        api.siteInfo().then(setInfo).catch(() => {});
    }, []);

    return (
        <div>
            <PageHeader
                eyebrow="VISIT US"
                title="Contact Choice Ayurveda"
                subtitle="Riley personally reviews every consultation request. Use the form below or reach out directly — we’d love to hear from you."
                breadcrumbs={[{ label: "Home", to: "/" }, { label: "Contact" }]}
            />

            <section className="py-12 sm:py-16">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                        <div className="lg:col-span-5" data-testid={SITE.contactDetails}>
                            <h2 className="font-display text-2xl text-moss">The clinic</h2>
                            <p className="mt-2 text-foreground/75">
                                Tucked among avocado and lilikoi groves in upcountry Maui.
                            </p>
                            <ul className="mt-6 space-y-4 text-sm text-foreground/85">
                                <li className="flex items-start gap-3 rounded-2xl border border-border/80 bg-white/80 p-4">
                                    <MapPin className="mt-0.5 h-5 w-5 text-moss shrink-0" strokeWidth={1.5} />
                                    <div>
                                        <p className="font-medium text-foreground/95">Address</p>
                                        <p>{info?.address}</p>
                                    </div>
                                </li>
                                <li className="flex items-start gap-3 rounded-2xl border border-border/80 bg-white/80 p-4">
                                    <Phone className="mt-0.5 h-5 w-5 text-moss shrink-0" strokeWidth={1.5} />
                                    <div>
                                        <p className="font-medium text-foreground/95">Phone</p>
                                        <a href={info?.phone_link} className="text-terracotta hover:underline">
                                            {info?.phone}
                                        </a>
                                    </div>
                                </li>
                                <li className="flex items-start gap-3 rounded-2xl border border-border/80 bg-white/80 p-4">
                                    <Mail className="mt-0.5 h-5 w-5 text-moss shrink-0" strokeWidth={1.5} />
                                    <div>
                                        <p className="font-medium text-foreground/95">Email</p>
                                        <a href={`mailto:${info?.email}`} className="text-terracotta hover:underline">
                                            {info?.email}
                                        </a>
                                    </div>
                                </li>
                                <li className="flex items-start gap-3 rounded-2xl border border-border/80 bg-white/80 p-4">
                                    <Clock className="mt-0.5 h-5 w-5 text-moss shrink-0" strokeWidth={1.5} />
                                    <div>
                                        <p className="font-medium text-foreground/95">Hours</p>
                                        <ul className="text-foreground/80">
                                            {info?.hours?.map((h) => (
                                                <li key={h.label}>
                                                    <span className="text-foreground/70">{h.label}: </span>
                                                    {h.value}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </li>
                            </ul>

                            <div className="mt-6 flex items-center gap-3">
                                {info?.socials?.map((s) => (
                                    <a
                                        key={s.name}
                                        href={s.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        aria-label={s.name}
                                        className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/80 border border-border/80 text-moss hover:text-terracotta hover:border-terracotta/50 transition-colors"
                                    >
                                        {s.icon === "facebook" ? (
                                            <Facebook className="h-4 w-4" strokeWidth={1.75} />
                                        ) : (
                                            <Instagram className="h-4 w-4" strokeWidth={1.75} />
                                        )}
                                    </a>
                                ))}
                            </div>

                            <div className="mt-8" data-testid={SITE.contactMap}>
                                <GhlEmbedPlaceholder kind="map" />
                            </div>
                        </div>
                        <div className="lg:col-span-7" data-testid={SITE.contactGhlForm}>
                            <h2 className="font-display text-2xl text-moss">Request a consultation</h2>
                            <p className="mt-2 text-foreground/75">
                                Share your information and we’ll be in touch within 1–2 business days.
                            </p>
                            <div className="mt-6">
                                <GhlEmbedPlaceholder kind="consultation-form" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
